import { sha256 } from "../logic/tokens";
import { IDENTITY_POLICY } from "../policy";
import type { IdentityApi, ShareLinkResult } from "../types";
import type { Ctx } from "./context";
import { DEMO_SHARE_LINKS, shareExpiry, type DemoShareLink } from "./demo-content";

/**
 * Share links resolve to review packages (FR-COLLAB-101–107, 162). Tokens and passwords are compared only as
 * SHA-256 hashes here; the identity service stores token hashes and checks passwords with Argon2id (FR-ADMIN-032).
 * Link holders never get an account.
 */

type ShareApi = Pick<IdentityApi, "openShareLink" | "unlockShareLink">;

interface LinkRecord {
  link: DemoShareLink;
  tokenHash: string;
  passwordHash: string | undefined;
  expiresAt: number;
}

const FIRM_NAME = "Northlight Studio";

export function createShareApi(ctx: Ctx, firmName: string = FIRM_NAME): ShareApi {
  const createdAt = ctx.now();
  /** Links unlocked with their password in this page; a reload asks again. */
  const unlocked = new Set<string>();
  const records: Promise<LinkRecord[]> = Promise.all(
    DEMO_SHARE_LINKS.map(async (link) => ({
      link,
      tokenHash: await sha256(link.token),
      // Salted with the link id so equal passwords on two links hash differently.
      passwordHash:
        link.password === undefined ? undefined : await sha256(`${link.linkId}:${link.password}`),
      expiresAt: shareExpiry(link, createdAt),
    })),
  );

  const find = async (token: string) => {
    const hash = await sha256(token);
    return (await records).find((record) => record.tokenHash === hash);
  };

  const failures = (linkId: string) => {
    const now = ctx.now();
    const recent = (ctx.state.shareFailures[linkId] ?? []).filter(
      (at) => now - at < IDENTITY_POLICY.share.lockMs,
    );
    ctx.state.shareFailures[linkId] = recent;
    return recent;
  };

  const resolve = (record: LinkRecord, wrong: boolean): ShareLinkResult => {
    const { link } = record;
    if (link.revoked) return { status: "revoked", firmName, senderName: link.senderName };
    if (ctx.now() >= record.expiresAt)
      return { status: "expired", firmName, senderName: link.senderName };
    if (record.passwordHash !== undefined && !unlocked.has(link.linkId)) {
      const recent = failures(link.linkId);
      if (recent.length >= IDENTITY_POLICY.share.maxAttempts) {
        const until = (recent[0] ?? ctx.now()) + IDENTITY_POLICY.share.lockMs;
        return { status: "locked", firmName, until: new Date(until).toISOString() };
      }
      return {
        status: "password",
        firmName,
        attemptsLeft: IDENTITY_POLICY.share.maxAttempts - recent.length,
        wrong,
      };
    }
    return {
      status: "ok",
      package: {
        linkId: link.linkId,
        title: link.title,
        message: link.message,
        projectName: link.projectName,
        firmName,
        senderName: link.senderName,
        items: link.items.map((item) => ({ ...item })),
        rights: { ...link.rights },
        expiresAt: new Date(record.expiresAt).toISOString(),
        watermark: `${firmName} · Not for construction`,
      },
    };
  };

  return {
    async openShareLink(token) {
      await ctx.wait();
      const record = await find(token);
      return record === undefined ? { status: "notFound" } : resolve(record, false);
    },

    async unlockShareLink(token, password) {
      await ctx.wait();
      const record = await find(token);
      if (record === undefined) return { status: "notFound" };
      const before = resolve(record, false);
      // A locked link refuses the password without checking it (FR-COLLAB-103).
      if (before.status !== "password") return before;
      if ((await sha256(`${record.link.linkId}:${password}`)) === record.passwordHash) {
        unlocked.add(record.link.linkId);
        ctx.state.shareFailures[record.link.linkId] = [];
        ctx.persist();
        return resolve(record, false);
      }
      ctx.state.shareFailures[record.link.linkId] = [...failures(record.link.linkId), ctx.now()];
      ctx.persist();
      return resolve(record, true);
    },
  };
}
